import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

// Portal wrapper — escapes any broken stacking context (transform/filter ancestors)
// and locks body scroll while the modal is open.
const ModalShell = ({ onClose, maxWidth = "sm:max-w-lg", children }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-stretch sm:items-center justify-center sm:p-4"
      onClick={onClose}
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div
        className={`bg-white sm:rounded-2xl w-full ${maxWidth} max-h-[100dvh] sm:max-h-[92vh] flex flex-col overflow-hidden`}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

// ============== Product Library ==============
export const ChemicalsLibrary = ({ slug }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tenants/${slug}/chemicals`, { headers: authHeaders() });
      setItems(r.data || []);
    } catch { toast.error("Failed to load products"); }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  const save = async (data) => {
    setLoading(true);
    try {
      if (editing?.id) {
        await axios.patch(`${API}/tenants/${slug}/chemicals/${editing.id}`, data, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/tenants/${slug}/chemicals`, data, { headers: authHeaders() });
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/tenants/${slug}/chemicals/${id}`, { headers: authHeaders() });
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="chemicals-library">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Products</h3>
        <Button size="sm" onClick={() => setEditing({})} data-testid="add-chemical-btn">+ Add Product</Button>
      </div>
      {items.length === 0 && <p className="text-sm text-gray-500">No products yet. Add your first one so you don't have to retype EPA numbers every time.</p>}
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="flex items-start justify-between p-3 rounded-lg border bg-white">
            <div className="min-w-0">
              <div className="font-medium text-sm text-gray-900 truncate">{it.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {it.epa_reg_number ? `EPA ${it.epa_reg_number}` : ""}
                {it.default_rate ? ` · ${it.default_rate} ${it.rate_unit || ""}` : ""}
                {it.rei_hours ? ` · REI ${it.rei_hours}h` : ""}
              </div>
            </div>
            <div className="flex gap-2 shrink-0 ml-2">
              <button onClick={() => setEditing(it)} className="text-xs text-slate-900">Edit</button>
              <button onClick={() => del(it.id)} className="text-xs text-red-500">Delete</button>
            </div>
          </div>
        ))}
      </div>
      {editing && <ChemicalFormModal initial={editing} onClose={() => setEditing(null)} onSave={save} loading={loading} />}
    </div>
  );
};

const ChemicalFormModal = ({ initial, onClose, onSave, loading }) => {
  const [f, setF] = useState({
    name: initial.name || "",
    epa_reg_number: initial.epa_reg_number || "",
    manufacturer: initial.manufacturer || "",
    signal_word: initial.signal_word || "",
    active_ingredient: initial.active_ingredient || "",
    default_rate: initial.default_rate || "",
    rate_unit: initial.rate_unit || "",
    rei_hours: initial.rei_hours || "",
    notes: initial.notes || "",
    active: initial.active !== false,
  });
  return (
    <ModalShell onClose={onClose} maxWidth="sm:max-w-md">
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 space-y-3">
        <h3 className="text-lg font-semibold">{initial.id ? "Edit Product" : "Add Product"}</h3>
        <Label className="text-xs">Product Name *</Label>
        <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Prodiamine 65WDG" data-testid="chem-name" />
        <Label className="text-xs">EPA Reg #</Label>
        <Input value={f.epa_reg_number} onChange={e => setF({ ...f, epa_reg_number: e.target.value })} placeholder="70506-42" />
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <Label className="text-xs">Default Rate</Label>
            <Input type="number" step="0.01" value={f.default_rate} onChange={e => setF({ ...f, default_rate: e.target.value })} placeholder="0.18" className="w-full" />
          </div>
          <div className="min-w-0">
            <Label className="text-xs">Unit</Label>
            <Input value={f.rate_unit} onChange={e => setF({ ...f, rate_unit: e.target.value })} placeholder="oz/1000sqft" className="w-full" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="min-w-0">
            <Label className="text-xs">REI (hours)</Label>
            <Input type="number" step="0.5" value={f.rei_hours} onChange={e => setF({ ...f, rei_hours: e.target.value })} placeholder="12" className="w-full" />
          </div>
          <div className="min-w-0">
            <Label className="text-xs">Signal Word</Label>
            <select value={f.signal_word} onChange={e => setF({ ...f, signal_word: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm h-10">
              <option value="">—</option>
              <option value="Caution">Caution</option>
              <option value="Warning">Warning</option>
              <option value="Danger">Danger</option>
            </select>
          </div>
        </div>
        <Label className="text-xs">Active Ingredient</Label>
        <Input value={f.active_ingredient} onChange={e => setF({ ...f, active_ingredient: e.target.value })} placeholder="Prodiamine 65%" />
        <Label className="text-xs">Manufacturer</Label>
        <Input value={f.manufacturer} onChange={e => setF({ ...f, manufacturer: e.target.value })} placeholder="Quali-Pro" />
        <Label className="text-xs">Notes</Label>
        <Input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="Internal notes" />
      </div>
      <div className="border-t bg-white px-5 py-3 flex gap-2 shrink-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}>
        <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
        <Button className="flex-1 bg-slate-900 hover:bg-slate-900" disabled={loading || !f.name} onClick={() => onSave({
          ...f,
          default_rate: f.default_rate ? parseFloat(f.default_rate) : null,
          rei_hours: f.rei_hours ? parseFloat(f.rei_hours) : null,
        })} data-testid="save-chem-btn">
          {loading ? "..." : "Save"}
        </Button>
      </div>
    </ModalShell>
  );
};

// ============== Mixtures ==============
export const ChemicalMixtures = ({ slug, chemicals }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/tenants/${slug}/chemical-mixtures`, { headers: authHeaders() });
      setItems(r.data || []);
    } catch { toast.error("Failed to load mixtures"); }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  const save = async (data) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.patch(`${API}/tenants/${slug}/chemical-mixtures/${editing.id}`, data, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/tenants/${slug}/chemical-mixtures`, data, { headers: authHeaders() });
      }
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this mixture?")) return;
    try {
      await axios.delete(`${API}/tenants/${slug}/chemical-mixtures/${id}`, { headers: authHeaders() });
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="chemicals-mixtures">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Mixtures</h3>
        <Button size="sm" onClick={() => setEditing({ components: [] })} data-testid="add-mixture-btn">+ Add Mixture</Button>
      </div>
      {items.length === 0 && <p className="text-sm text-gray-500">No mixtures yet. Combine multiple products into a reusable blend.</p>}
      <div className="space-y-2">
        {items.map(m => (
          <div key={m.id} className="p-3 rounded-lg border bg-white">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">{m.name}</div>
                <div className="text-xs text-gray-500">{(m.components || []).length} product{(m.components || []).length !== 1 ? "s" : ""}</div>
              </div>
              <div className="flex gap-2 shrink-0 ml-2">
                <button onClick={() => setEditing(m)} className="text-xs text-slate-900">Edit</button>
                <button onClick={() => del(m.id)} className="text-xs text-red-500">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <MixtureFormModal initial={editing} chemicals={chemicals} onClose={() => setEditing(null)} onSave={save} loading={saving} />}
    </div>
  );
};

const MixtureFormModal = ({ initial, chemicals, onClose, onSave, loading }) => {
  const [f, setF] = useState({
    name: initial.name || "",
    notes: initial.notes || "",
    components: initial.components || [],
  });
  const addRow = () => setF(s => ({ ...s, components: [...s.components, { chemical_id: "", ratio_pct: "", rate_per_1k: "" }] }));
  const updateRow = (i, k, v) => setF(s => ({ ...s, components: s.components.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const removeRow = (i) => setF(s => ({ ...s, components: s.components.filter((_, idx) => idx !== i) }));
  const totalPct = (f.components || []).reduce((s, c) => s + (parseFloat(c.ratio_pct) || 0), 0);
  return (
    <ModalShell onClose={onClose} maxWidth="sm:max-w-lg">
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-5 space-y-3">
        <h3 className="text-lg font-semibold">{initial.id ? "Edit Mixture" : "Add Mixture"}</h3>
        <Label className="text-xs">Name *</Label>
        <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Spring Pre-Emergent Blend" data-testid="mixture-name" />
        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">Products in this mix</Label>
            <span className={`text-xs ${Math.abs(totalPct - 100) < 0.1 ? "text-blue-700" : "text-gray-500"}`}>
              Total: {totalPct.toFixed(0)}%
            </span>
          </div>
          <div className="space-y-2">
            {(f.components || []).map((c, i) => (
              <div key={i} className="p-2 rounded-lg border bg-gray-50 space-y-1.5">
                <div className="flex items-center gap-2">
                  <select value={c.chemical_id} onChange={e => updateRow(i, "chemical_id", e.target.value)} className="flex-1 min-w-0 border rounded-lg px-2 py-2 text-xs h-9 bg-white">
                    <option value="">Select product…</option>
                    {chemicals.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
                  </select>
                  <button type="button" onClick={() => removeRow(i)} className="text-red-500 text-lg px-2 shrink-0">×</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" step="1" placeholder="% of mix" value={c.ratio_pct} onChange={e => updateRow(i, "ratio_pct", e.target.value)} className="h-9 text-xs min-w-0 w-full" />
                  <Input type="number" step="0.01" placeholder="Rate/1000sqft" value={c.rate_per_1k} onChange={e => updateRow(i, "rate_per_1k", e.target.value)} className="h-9 text-xs min-w-0 w-full" />
                </div>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addRow} className="mt-2">+ Add Product</Button>
        </div>
        <Label className="text-xs">Notes</Label>
        <Input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} />
      </div>
      <div className="border-t bg-white px-5 py-3 flex gap-2 shrink-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}>
        <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
        <Button className="flex-1 bg-slate-900 hover:bg-slate-900" disabled={loading || !f.name} onClick={() => onSave({
          ...f,
          components: (f.components || []).map(c => ({
            chemical_id: c.chemical_id,
            ratio_pct: c.ratio_pct === "" ? null : parseFloat(c.ratio_pct),
            rate_per_1k: c.rate_per_1k === "" ? null : parseFloat(c.rate_per_1k),
          })).filter(c => c.chemical_id),
        })} data-testid="save-mixture-btn">
          {loading ? "..." : "Save"}
        </Button>
      </div>
    </ModalShell>
  );
};

// ============== Log Application ==============
export const LogApplicationModal = ({ slug, onClose, onSaved, prefill = {}, chemicals, mixtures, tenantState, tenantLicense }) => {
  const [f, setF] = useState({
    date: prefill.date || new Date().toISOString().split("T")[0],
    start_time: prefill.start_time || new Date().toTimeString().slice(0, 5),
    end_time: prefill.end_time || "",
    customer_name: prefill.customer_name || "",
    address: prefill.address || "",
    applicator_name: prefill.applicator_name || "",
    applicator_license: prefill.applicator_license || tenantLicense || "",
    area_sqft: prefill.area_sqft || "",
    job_instance_id: prefill.job_instance_id || null,
    lead_id: prefill.lead_id || null,
    state_code: prefill.state_code || tenantState || "",
    notes: prefill.notes || "",
    mixture_id: prefill.mixture_id || "",
    weather: prefill.weather || null,
    products: prefill.products && prefill.products.length > 0 ? prefill.products : [],
    state_extra_fields: prefill.state_extra_fields || {},
  });
  const [stateFields, setStateFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingWx, setLoadingWx] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!f.state_code) { setStateFields([]); return; }
    axios.get(`${API}/chemicals/state-fields/${f.state_code}`).then(r => setStateFields(r.data?.fields || [])).catch(() => setStateFields([]));
  }, [f.state_code]);

  const addProduct = (chem) => {
    setF(s => ({
      ...s,
      products: [
        ...s.products,
        {
          chemical_id: chem?.id || null,
          name: chem?.name || "",
          epa_reg_number: chem?.epa_reg_number || "",
          quantity: chem?.default_rate || "",
          unit: (chem?.rate_unit || "oz").split("/")[0],
          rate_applied: chem?.default_rate ? `${chem.default_rate} ${chem.rate_unit || ""}` : "",
          rei_hours: chem?.rei_hours || null,
        },
      ],
    }));
  };
  const updateProd = (i, k, v) => setF(s => ({ ...s, products: s.products.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const removeProd = (i) => setF(s => ({ ...s, products: s.products.filter((_, idx) => idx !== i) }));

  const applyMixture = (mixId) => {
    const mix = mixtures.find(m => m.id === mixId);
    if (!mix) return;
    const prods = (mix.components || []).map(c => {
      const ch = chemicals.find(x => x.id === c.chemical_id);
      return ch ? {
        chemical_id: ch.id, name: ch.name, epa_reg_number: ch.epa_reg_number || "",
        quantity: c.rate_per_1k || ch.default_rate || "",
        unit: (ch.rate_unit || "oz").split("/")[0],
        rate_applied: `${c.rate_per_1k || ch.default_rate || ""} ${ch.rate_unit || ""}`,
        rei_hours: ch.rei_hours || null,
      } : null;
    }).filter(Boolean);
    setF(s => ({ ...s, mixture_id: mixId, products: prods }));
  };

  const fetchWeather = async () => {
    // Try browser geolocation; if not granted, skip
    if (!navigator.geolocation) { toast.info("Location not available"); return; }
    setLoadingWx(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await axios.get(`${API}/weather/current?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
        setF(s => ({ ...s, weather: r.data }));
        if (r.data?.temp_f == null) toast.info("Weather unavailable right now — fill manually");
      } catch { toast.error("Weather fetch failed"); }
      finally { setLoadingWx(false); }
    }, () => { setLoadingWx(false); toast.info("Location denied — skipping auto-weather"); });
  };

  const save = async () => {
    if (f.products.length === 0) { toast.error("Add at least one product"); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        area_sqft: f.area_sqft ? parseFloat(f.area_sqft) : null,
        products: f.products.map(p => ({
          ...p,
          quantity: parseFloat(p.quantity) || 0,
          rei_hours: p.rei_hours ? parseFloat(p.rei_hours) : null,
        })),
      };
      await axios.post(`${API}/tenants/${slug}/chemical-applications`, payload, { headers: authHeaders() });
      toast.success("Application logged");
      onSaved?.();
      onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} maxWidth="sm:max-w-lg">
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between shrink-0">
          <h3 className="text-base font-semibold">Log Application</h3>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none px-2">×</button>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 space-y-3" style={{ WebkitOverflowScrolling: "touch" }}>
          {/* Summary strip — one line, tap to edit */}
          <button
            type="button"
            onClick={() => setShowDetails(v => !v)}
            className="w-full text-left rounded-lg border bg-gray-50 px-3 py-2 hover:bg-gray-100"
            data-testid="toggle-details-btn"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 text-xs text-gray-700 truncate">
                <span className="font-medium text-gray-900">
                  {f.customer_name || "No customer"}
                </span>
                <span className="text-gray-500"> · {f.date}{f.start_time ? ` ${f.start_time}` : ""}</span>
                {f.applicator_name ? <span className="text-gray-500"> · {f.applicator_name}</span> : null}
              </div>
              <span className="text-xs text-slate-900 shrink-0">{showDetails ? "Hide" : "Edit"}</span>
            </div>
          </button>

          {showDetails && (
            <div className="space-y-2 rounded-lg border p-3 bg-white">
              <div className="grid grid-cols-2 gap-2">
                <div className="min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Date</Label>
                  <Input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} data-testid="log-date" className="w-full h-9 text-sm" />
                </div>
                <div className="min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Area (sqft)</Label>
                  <Input type="number" value={f.area_sqft} onChange={e => setF({ ...f, area_sqft: e.target.value })} placeholder="8500" className="w-full h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Start</Label>
                  <Input type="time" value={f.start_time} onChange={e => setF({ ...f, start_time: e.target.value })} className="w-full h-9 text-sm" />
                </div>
                <div className="min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">End</Label>
                  <Input type="time" value={f.end_time} onChange={e => setF({ ...f, end_time: e.target.value })} className="w-full h-9 text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-gray-500">Customer</Label>
                <Input value={f.customer_name} onChange={e => setF({ ...f, customer_name: e.target.value })} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide text-gray-500">Address</Label>
                <Input value={f.address} onChange={e => setF({ ...f, address: e.target.value })} className="h-9 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">Applicator</Label>
                  <Input value={f.applicator_name} onChange={e => setF({ ...f, applicator_name: e.target.value })} className="w-full h-9 text-sm" />
                </div>
                <div className="min-w-0">
                  <Label className="text-[10px] uppercase tracking-wide text-gray-500">License #</Label>
                  <Input value={f.applicator_license} onChange={e => setF({ ...f, applicator_license: e.target.value })} className="w-full h-9 text-sm" />
                </div>
              </div>
            </div>
          )}

          {/* Mixture picker */}
          {mixtures && mixtures.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-gray-500">Use a Mixture</Label>
              <select value={f.mixture_id} onChange={e => applyMixture(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm h-9" data-testid="mixture-picker">
                <option value="">— None —</option>
                {mixtures.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}

          {/* Products — always visible, primary focus */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <Label className="text-xs font-medium text-gray-800">Products Applied *</Label>
              <select
                onChange={e => { if (e.target.value) { addProduct(chemicals.find(c => c.id === e.target.value)); e.target.value = ""; } }}
                className="text-xs border rounded-lg px-2 py-1 max-w-full bg-blue-50 text-slate-900 border-blue-200"
                data-testid="add-product-picker"
              >
                <option value="">+ Add from library</option>
                {chemicals.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              {f.products.length === 0 && (
                <div className="rounded-lg border-2 border-dashed border-gray-200 p-4 text-center">
                  <p className="text-xs text-gray-500">Tap "+ Add from library" to log what you applied.</p>
                </div>
              )}
              {f.products.map((p, i) => (
                <div key={i} className="p-2 rounded-lg border bg-white space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate min-w-0">{p.name || "Unnamed"}</span>
                    <button onClick={() => removeProd(i)} className="text-xs text-red-500 shrink-0">Remove</button>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <Input type="number" step="0.01" placeholder="Qty" value={p.quantity} onChange={e => updateProd(i, "quantity", e.target.value)} className="h-9 text-xs min-w-0 w-full" />
                    <Input placeholder="Unit" value={p.unit} onChange={e => updateProd(i, "unit", e.target.value)} className="h-9 text-xs min-w-0 w-full" />
                    <Input type="number" step="0.5" placeholder="REI h" value={p.rei_hours ?? ""} onChange={e => updateProd(i, "rei_hours", e.target.value)} className="h-9 text-xs min-w-0 w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* More options collapsed by default */}
          <button
            type="button"
            onClick={() => setShowMore(v => !v)}
            className="w-full text-xs text-gray-600 underline"
            data-testid="toggle-more-btn"
          >
            {showMore ? "Hide weather & notes" : "+ Weather, notes & extras"}
          </button>

          {showMore && (
            <div className="space-y-3">
              {/* Weather */}
              <div className="rounded-lg border p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">Weather</span>
                  <button type="button" onClick={fetchWeather} disabled={loadingWx} className="text-xs text-slate-900" data-testid="auto-wx-btn">
                    {loadingWx ? "Fetching…" : "📍 Auto-fill"}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input type="number" step="0.1" placeholder="°F" value={f.weather?.temp_f ?? ""} onChange={e => setF(s => ({ ...s, weather: { ...(s.weather || {}), temp_f: e.target.value ? parseFloat(e.target.value) : null } }))} className="min-w-0 w-full h-9 text-xs" />
                  <Input type="number" step="0.1" placeholder="mph" value={f.weather?.wind_mph ?? ""} onChange={e => setF(s => ({ ...s, weather: { ...(s.weather || {}), wind_mph: e.target.value ? parseFloat(e.target.value) : null } }))} className="min-w-0 w-full h-9 text-xs" />
                  <Input placeholder="Dir" value={f.weather?.wind_dir ?? ""} onChange={e => setF(s => ({ ...s, weather: { ...(s.weather || {}), wind_dir: e.target.value } }))} className="min-w-0 w-full h-9 text-xs" />
                </div>
              </div>

              {/* State-specific extras */}
              {stateFields.length > 0 && (
                <div className="rounded-lg border p-3 bg-amber-50">
                  <div className="text-xs font-medium text-amber-900 mb-2">{f.state_code} — State fields</div>
                  {stateFields.map(sf => (
                    <div key={sf.key} className="mb-2 last:mb-0">
                      <Label className="text-[10px] uppercase tracking-wide text-gray-600">{sf.label}</Label>
                      {sf.type === "boolean" ? (
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={!!(f.state_extra_fields || {})[sf.key]} onChange={e => setF(s => ({ ...s, state_extra_fields: { ...(s.state_extra_fields || {}), [sf.key]: e.target.checked } }))} />
                          <span className="text-gray-700">Yes</span>
                        </label>
                      ) : (
                        <Input value={(f.state_extra_fields || {})[sf.key] || ""} onChange={e => setF(s => ({ ...s, state_extra_fields: { ...(s.state_extra_fields || {}), [sf.key]: e.target.value } }))} className="h-9 text-sm" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <Label className="text-[10px] uppercase tracking-wide text-gray-500">Notes</Label>
                <Input value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder="Conditions, observations…" className="h-9 text-sm" />
              </div>
            </div>
          )}
        </div>
        <div className="bg-white border-t px-4 py-3 flex gap-2 shrink-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}>
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-blue-700 hover:bg-blue-800" onClick={save} disabled={saving} data-testid="log-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
    </ModalShell>
  );
};

// ============== History ==============
export const ChemicalHistory = ({ slug, onDuplicate }) => {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/tenants/${slug}/chemical-applications`, { headers: authHeaders() });
      setItems(r.data || []);
    } catch { toast.error("Failed to load history"); }
  };
  useEffect(() => { load(); }, [slug]); // eslint-disable-line

  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/tenants/${slug}/chemical-applications/${id}/duplicate`, {}, { headers: authHeaders() });
      toast.success("Duplicated as today's application");
      await load();
      onDuplicate?.();
    } catch { toast.error("Duplicate failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this application record?")) return;
    try {
      await axios.delete(`${API}/tenants/${slug}/chemical-applications/${id}`, { headers: authHeaders() });
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="chemical-history">
      <h3 className="text-base font-semibold text-gray-900">History</h3>
      {items.length === 0 && <p className="text-sm text-gray-500">No applications logged yet.</p>}
      <div className="space-y-2">
        {items.map(a => (
          <div key={a.id} className="p-3 rounded-lg border bg-white">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">{a.customer_name || "—"}</div>
                <div className="text-xs text-gray-500 truncate">
                  {a.date}{a.start_time ? ` · ${a.start_time}` : ""}
                  {a.rei_hours ? ` · REI ${a.rei_hours}h` : ""}
                  {` · ${(a.products || []).length} product${(a.products || []).length !== 1 ? "s" : ""}`}
                </div>
                <div className="text-xs text-gray-400 truncate">{a.address}</div>
              </div>
              <div className="flex gap-2 shrink-0 ml-2">
                <button onClick={() => duplicate(a.id)} className="text-xs text-slate-900">Same Again</button>
                <button onClick={() => del(a.id)} className="text-xs text-red-500">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== Main Chemicals Tab ==============
export const ChemicalsTab = ({ slug, business, initialPrefill }) => {
  const [tab, setTab] = useState("log");
  const [chemicals, setChemicals] = useState([]);
  const [mixtures, setMixtures] = useState([]);
  const [showLog, setShowLog] = useState(!!initialPrefill);
  const [prefill, setPrefill] = useState(initialPrefill || {});

  const loadAll = async () => {
    try {
      const [c, m] = await Promise.all([
        axios.get(`${API}/tenants/${slug}/chemicals`, { headers: authHeaders() }),
        axios.get(`${API}/tenants/${slug}/chemical-mixtures`, { headers: authHeaders() }),
      ]);
      setChemicals(c.data || []);
      setMixtures(m.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAll(); }, [slug]); // eslint-disable-line

  const tenantState = business?.state_code || "";
  const tenantLicense = business?.applicator_license || "";

  return (
    <div className="space-y-4" data-testid="chemicals-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Chemicals</h2>
        <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => { setPrefill({}); setShowLog(true); }} data-testid="open-log-btn">
          + Log Application
        </Button>
      </div>

      <div className="flex gap-1 border-b overflow-x-auto">
        {[
          ["log", "History"],
          ["products", "Products"],
          ["mixtures", "Mixtures"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${tab === k ? "text-slate-900 border-b-2 border-slate-900" : "text-gray-500"}`} data-testid={`chem-tab-${k}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "log" && <ChemicalHistory slug={slug} onDuplicate={loadAll} />}
      {tab === "products" && <ChemicalsLibrary slug={slug} />}
      {tab === "mixtures" && <ChemicalMixtures slug={slug} chemicals={chemicals} />}

      {showLog && (
        <LogApplicationModal
          slug={slug}
          chemicals={chemicals}
          mixtures={mixtures}
          tenantState={tenantState}
          tenantLicense={tenantLicense}
          prefill={prefill}
          onClose={() => setShowLog(false)}
          onSaved={() => loadAll()}
        />
      )}
    </div>
  );
};

// ============== Settings Form Block (reusable) ==============
export const ChemicalsSettingsBlock = ({ formData, setFormData }) => {
  return (
    <div className="space-y-3 rounded-lg border p-3 bg-gray-50" data-testid="chemicals-settings-block">
      <div className="flex items-start gap-3">
        <input
          id="chem-enabled"
          type="checkbox"
          className="mt-1 h-4 w-4 accent-blue-700"
          checked={!!formData.chemicals_enabled}
          onChange={e => setFormData({ ...formData, chemicals_enabled: e.target.checked })}
          data-testid="chem-enabled-toggle"
        />
        <label htmlFor="chem-enabled" className="text-sm text-gray-700 leading-snug">
          <span className="font-medium text-gray-900">Enable chemical tracking</span>
          <br />
          Adds a Chemicals tab for logging pesticide/fertilizer applications with state-compliant records.
        </label>
      </div>
      {formData.chemicals_enabled && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div>
            <Label className="text-xs">State</Label>
            <select value={formData.state_code || ""} onChange={e => setFormData({ ...formData, state_code: e.target.value })} className="w-full border rounded-lg px-2 py-2 text-sm h-10" data-testid="state-picker">
              <option value="">—</option>
              {US_STATES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Applicator License #</Label>
            <Input value={formData.applicator_license || ""} onChange={e => setFormData({ ...formData, applicator_license: e.target.value })} placeholder="QAL-12345" data-testid="applicator-license" />
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Public Invoice Display ==============
export const InvoiceApplicationRecord = ({ quoteId, token }) => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!quoteId || !token) { setLoading(false); return; }
    axios.get(`${API}/public/quotes/${quoteId}/chemical-applications?token=${token}`)
      .then(r => setApps(r.data || []))
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  }, [quoteId, token]);

  if (loading) return null;
  if (!apps || apps.length === 0) return null;

  return (
    <div className="px-8 pb-6" data-testid="invoice-application-record">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-blue-700">🌿</span>
          <h3 className="text-base font-semibold text-slate-900">Application Record</h3>
        </div>
        {apps.map(a => {
          const safeAt = a.reentry_safe_at ? new Date(a.reentry_safe_at) : null;
          return (
            <div key={a.id} className="border-t border-blue-200 pt-3 mt-3 first:border-0 first:pt-0 first:mt-0">
              <div className="text-sm font-medium text-slate-900">
                {a.date}{a.start_time ? ` at ${a.start_time}` : ""}
              </div>
              {a.applicator_name && (
                <div className="text-xs text-slate-900">
                  Applied by {a.applicator_name}{a.applicator_license ? ` (Lic. ${a.applicator_license})` : ""}
                </div>
              )}
              {(a.products || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {a.products.map((p, i) => (
                    <li key={i} className="text-xs text-gray-700">
                      • <span className="font-medium">{p.name}</span>
                      {p.epa_reg_number ? ` (EPA ${p.epa_reg_number})` : ""}
                      {p.quantity ? ` — ${p.quantity} ${p.unit || ""}` : ""}
                      {p.rate_applied ? ` @ ${p.rate_applied}` : ""}
                    </li>
                  ))}
                </ul>
              )}
              {safeAt && (
                <div className="mt-2 text-xs text-slate-900">
                  <span className="font-medium">Safe to re-enter:</span>{" "}
                  {safeAt.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  {a.rei_hours ? ` (${a.rei_hours}h REI)` : ""}
                </div>
              )}
              {a.weather && (a.weather.temp_f != null || a.weather.wind_mph != null) && (
                <div className="mt-1 text-xs text-gray-500">
                  {a.weather.temp_f != null ? `${a.weather.temp_f}°F` : ""}
                  {a.weather.wind_mph != null ? ` · ${a.weather.wind_mph} mph ${a.weather.wind_dir || ""}` : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
